import gql from 'graphql-tag'

export default {
  GET_PROJECT_BOARDS: gql`
    query boards($projectUid: String!, $first: Int, $page: Int) {
      boards(project:{ keyword: [$projectUid] searchKey: UUID }, first: $first, page: $page) {
        data {
          uuid
          name
          order
          createdAt
          updatedAt
          milestones(
            first: 0
          ) {
            total
          }
          statuses {
            id
            key
          }
          members(first: -1) {
            data {
              uuid
              name
              avatar
            }
            total
          }
          tasks(first: 0) {
            total
          }
        }
        total
        currentPage
        lastPage
        hasMorePages 
      }
    }
  `,

  GET_BOARD: gql`
    query board(
      $uuid: UUID!, 
      $assignees: [UUID!], 
      $labelUids: [UUID!], 
      $statusKeys: [String!], 
      $priority: TaskPriority
      $search: TaskSearchInput
    ) {
      board(uuid: $uuid) {
        uuid
        name
        updatedAt
        createdAt
        project {
          uuid
          name
        }
        statuses {
          id
          key
          name
          color
          position
          isFinal
          tasks(
            first: 10
            assignees: $assignees
            labelUids: $labelUids
            statusKeys: $statusKeys
            priority: $priority
            search: $search
          ) {
            total
            hasMorePages
            data {
              uuid
              title
              description
              priority
              milestone {
                uuid
                name
                description
                deadline
              }
              status {
                id
                key
              }
              labels {
                uuid
                name
                color
              }
              assignee {
                uuid
                name
                avatar
              }
              creator {
                uuid
                name
              }
              dueDate
              timeEstimate
              createdAt
              updatedAt
            }
          }
        }
        members(first:-1) {
          total
          data {
            uuid
            name
            avatar
            email
          }
        }
        labels {
          data {
            uuid,
            name,
            color,
            description,
          }
        }
        milestones {
          data {
            uuid
            name
            description
            deadline
          }
        }
      }
    }
  `,

  GET_BOARD_TASKS: gql`
    query boardTasks($boardUid: UUID!, $statusId: ID, $first: Int, $page: Int) {
      board(uuid: $boardUid) {
        uuid
        name
        tasks(statusId: $statusId, first: $first, page: $page) {
          data {
            uuid
            title
            description
            milestone {
              uuid
              name
              description
              deadline
            }
            labels {
              uuid
              name
              color
            }
            status {
              id
              key
              name
              color
              isFinal
            }
            assignee {
              uuid
              name
              avatar
            }
            dueDate
            timeEstimate
            createdAt
          }
          total
          paginatorInfo {
            currentPage
            lastPage
            hasMorePages
          }
        }
      }
    }
  `,

  GET_BOARD_LABELS: gql`
    query boardLabels($boardUid: UUID!) {
      board(uuid: $boardUid) {
        labels {
          data {
            uuid,
            name,
            color,
            description,
          }
        }
      }
    }
  `,

  FETCH_TASK: gql`
    query task($uuid: UUID!){
      task(uuid:$uuid){
        uuid
        title
        description
        priority
        comments {
          uuid
          author {
            uuid
            name
            avatar
          }
          content
          mentions
          mentionedUsers {
            uuid
            name
          }
          createdAt
          updatedAt
        }
        labels {
          uuid
          name
          color
        }
        status {
          id
          key
          name
          color
          isFinal
        }
          assignee {
          uuid
          name
          avatar
        }
        creator {
          uuid
          name
        }
        milestone {
          uuid
          name
          description
          deadline
        }
        dueDate
        timeEstimate
        createdAt
        updatedAt
        activities {
          uuid
          actionType
          description
          createdAt
          user {
            uuid
            name
            avatar
          }
          ... on TaskAssignedActivity {
            newAssignee {
              uuid
              name
            }
            oldAssignee {
              uuid
              name
            }
          }
          ... on TaskStatusChangedActivity {
            oldStatus {
              name
              color
            }
            newStatus {
              name
              color
            }
          }
          ... on TaskLabelAttachedActivity {
            labels {
              name
              color
            }
          }
          ... on TaskLabelDetachedActivity {
            labels {
              name
              color
            }
          }
          ... on TaskMilestoneAttachedActivity {
            milestone {
              name
            }
            oldMilestone {
              name
            }
          }
          ... on TaskMilestoneDetachedActivity {
            milestone {
              name
            }
          }
          ... on TaskDueDateChangedActivity {
            oldDueDate
            newDueDate
          }
          ... on TaskMentionAddedActivity {
            mentionedUserCount
          }
          ... on TaskMentionChangedActivity {
            oldMentionedUserCount
            newMentionedUserCount
          }
        }
      }
  }
  `,
  GET_TASKS_BY_STATUS_KEY: gql`
    query getTasksByStatusKey(
      $boardUid: UUID!
      $statusKeys: [String!]
      $page: Int
      $assignees: [UUID!]
      $labelUids: [UUID!]
    ) {
      board(uuid: $boardUid) {
        tasks(
          statusKeys: $statusKeys
          page: $page
          first: 10
          assignees: $assignees
          labelUids: $labelUids
        ) {
          total
          hasMorePages
          data {
            uuid
            title
            description
            milestone {
              uuid
              name
              description
              deadline
            }
            status {
              id
              key
            }
            labels {
              uuid
              name
              color
            }
            assignee {
              uuid
              name
              avatar
            }
            creator {
              uuid
              name
            }
            dueDate
            createdAt
            updatedAt
          }
        }
      }
    }
  `,
  GET_MEMBER_PERFORMANCE: gql`
    query getMemberPerformance(
      $projectUid: UUID!
      $boardUid: UUID
      $memberUid: UUID!
      $timeRange: PresetTimeRangeInput!
    ) {
      memberPerformance(
        projectUid: $projectUid
        boardUid: $boardUid
        memberUid: $memberUid
        timeRange: $timeRange
      ) {
        totalTasksAssigned,
        totalTasksCompleted,
        totalTasksOverdue,
        averageCompletionTime,
        completionRate,
        tasksByStatus {
          category
          count
          percentage
          color
        }
        activityLevel
        contributionScore
      }
    }
  `,
  GET_TEAM_PERFORMANCE: gql`
    query getTeamPerformance(
      $projectUid: UUID!
      $boardUid: UUID
      $timeRange: PresetTimeRangeInput!
    ) {
      teamPerformance(
        projectUid: $projectUid
        boardUid: $boardUid
        timeRange: $timeRange
      ) {
        totalTasksCreated,
        totalTasksInProgress,
        totalTasksCompleted,
        averageCompletionTime,
        completionRate,
        tasksByStatus {
          category
          count
          percentage
          color
        }
        velocity
      },
    }
  `,
  GET_TEAM_AND_BOARD_PERFORMANCE: gql`
    query getTeamAndBoardPerformance(
      $projectUid: UUID!
      $boardUid: UUID!
      $timeRange: PresetTimeRangeInput!
    ) {
      teamPerformance(
        projectUid: $projectUid
        boardUid: $boardUid
        timeRange: $timeRange
      ) {
        totalTasksCreated,
        totalTasksInProgress,
        totalTasksCompleted,
        averageCompletionTime,
        completionRate,
        tasksByStatus {
          category
          count
          percentage
          color
        }
        velocity
      },
      boardPerformance(
        boardUid: $boardUid
        projectUid: $projectUid
      ) {
        taskTrend {
          month,
          monthLabel,
          tasksCreated,
          tasksCompleted,
          topActiveUser {
            uuid,
            name,
            avatar
          },
          topActiveUserActivityCount
        }
      }
    }
  `,
}
